import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios, { clearAuthContext, setActiveOrgId, initializeOrganization, getActiveOrgId } from "../lib/api";
import {
  BarChart3,
  LayoutDashboard,
  Plug,
  Bell,
  Code2,
  User,
  Users,
  LogOut,
  Menu,
  X,
  LineChart,
  ChevronDown,
  Calculator,
  CalendarRange,
  Database,
  Shield,
  Send,
  CreditCard,
  Clock,
  Building2,
  Check,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { API, useAuth } from "../App";

const navItems = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard" },
  {
    icon: LineChart,
    label: "Analytics",
    path: "/analytics",
    children: [
      { label: "GA4", path: "/analytics" },
      { label: "Marcas", path: "/analytics/brands" },
      { label: "Productos", path: "/analytics/products" },
      { label: "GAds — Productos", path: "/analytics/google-ads-products" },
      { label: "Meta — Productos", path: "/analytics/meta-ads-products" },
      { label: "Feed Optimizer", path: "/analytics/feed-optimizer" },
      { label: "Customer LTV", path: "/analytics/customer-ltv" },
      { label: "Funnel Opportunities", path: "/analytics/funnel-opportunities" },
      { label: "Stock", path: "/analytics/stock" },
      { label: "Comparar", path: "/compare" },
    ],
  },
  { icon: Calculator, label: "Costes Fijos", path: "/fixed-costs" },
  { icon: CalendarRange, label: "Resumen Anual", path: "/finance/annual" },
  { icon: Database, label: "Feed Datos", path: "/feed-datos" },
];

const platformItems = [
  { name: "Google Ads", path: "/platform/google_ads", color: "#4285F4" },
  { name: "Meta Ads", path: "/platform/meta_ads", color: "#1877F2" },
  { name: "TikTok Ads", path: "/platform/tiktok_ads", color: "#0A0A0A" },
  {
    name: "Awin",
    path: "/platform/awin",
    color: "#1A7A3C",
    children: [{ label: "Transacciones", path: "/platform/awin/transactions" }],
  },
];

const DashboardLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, orgRole } = useAuth();
  const isOwner = !orgRole || orgRole === "owner"; // default to owner if unknown (backwards compat)
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedPlatform, setExpandedPlatform] = useState(null);
  const [trialInfo, setTrialInfo] = useState(null); // { daysLeft, endingSoon }
  const [orgs, setOrgs] = useState([]); // list of orgs user belongs to
  const [activeOrgName, setActiveOrgName] = useState("");

  const getDefaultExpanded = () => {
    for (const p of platformItems) {
      if (p.children && p.children.some((c) => location.pathname.startsWith(c.path))) {
        return p.path;
      }
    }
    return null;
  };

  const [initialized, setInitialized] = useState(false);
  if (!initialized) {
    setExpandedPlatform(getDefaultExpanded());
    setInitialized(true);
  }

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (_) {}
    localStorage.removeItem("auth_token");
    clearAuthContext();
    toast.success("Sesión cerrada");
    navigate("/login");
  };

  // Fetch trial status to show badge
  useEffect(() => {
    const fetchTrialStatus = async () => {
      try {
        const token = localStorage.getItem("auth_token");
        const res = await axios.get(`${API}/billing/subscription-status`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
        });
        const sub = res.data;
        if (sub.is_trialing && sub.trial_end) {
          const msLeft = new Date(sub.trial_end) - Date.now();
          const daysLeft = Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24)));
          setTrialInfo({ daysLeft, endingSoon: sub.trial_ending_soon || daysLeft <= 3 });
        }
      } catch (_) {}
    };
    fetchTrialStatus();
  }, []);

  // Fetch user's organizations for the org selector (only needed if user has 2+)
  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const token = localStorage.getItem("auth_token");
        const res = await axios.get(`${API}/orgs`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
        });
        const list = res.data.organizations || [];
        setOrgs(list);
        // Set active org name for display
        const activeId = getActiveOrgId();
        const current = list.find(o => o.organization_id === activeId);
        if (current) setActiveOrgName(current.name);
      } catch (_) {}
    };
    fetchOrgs();
  }, []);

  const handleOrgSwitch = async (orgId) => {
    if (!orgId || orgId === getActiveOrgId()) return;
    try {
      const token = localStorage.getItem("auth_token");
      setActiveOrgId(orgId, user?.user_id);
      await initializeOrganization(user?.user_id);
      // Reload page to refresh all data for the new org
      window.location.href = "/dashboard";
    } catch (_) {
      toast.error("No se pudo cambiar de organización");
    }
  };

  // ── Nav item renderer ────────────────────────────────────────────────
  const NavLink = ({ item, onClick }) => {
    const isActive =
      item.path === "/analytics"
        ? location.pathname.startsWith("/analytics") || location.pathname === "/compare"
        : location.pathname === item.path;
    return (
      <Link
        to={item.path}
        onClick={onClick}
        data-testid={`nav-${item.path.replace("/", "")}`}
        className={`relative flex items-center gap-3 pl-5 pr-3 py-2 text-[13px] transition-colors ${
          isActive
            ? "text-[#0A0A0A] font-semibold before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-[3px] before:h-4 before:bg-[#C8192C] before:rounded-r-sm"
            : "text-[#888888] hover:text-[#0A0A0A]"
        }`}
      >
        <item.icon className="w-[15px] h-[15px]" strokeWidth={1.75} />
        <span>{item.label}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-50 glass-effect px-4 py-3">
        <div className="flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-[22px] h-[22px] bg-[#C8192C] rounded-[4px] flex items-center justify-center">
              <BarChart3 className="w-3 h-3 text-white" strokeWidth={2.5} />
            </div>
            <span style={{ fontFamily: "Chivo", fontWeight: 900, fontSize: "14px", letterSpacing: "-0.03em" }}>
              ProfitSystem
            </span>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="mobile-menu-btn"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/30 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full w-[210px] bg-white border-r border-[#E8E8E5] flex flex-col
          transform transition-transform duration-200 ease-in-out
          lg:translate-x-0
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
        `}
        data-testid="dashboard-sidebar"
      >
        {/* Logo */}
        <div className="px-5 py-[18px] border-b border-[#F0F0ED] flex items-center gap-2">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-[22px] h-[22px] bg-[#C8192C] rounded-[4px] flex items-center justify-center flex-shrink-0">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="2 12 6 12 9 4 15 20 18 12 22 12" />
              </svg>
            </div>
            <span
              className="text-[#0A0A0A]"
              style={{ fontFamily: "Chivo", fontWeight: 900, fontSize: "14px", letterSpacing: "-0.03em" }}
            >
              ProfitSystem
            </span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-3 overflow-y-auto" data-testid="sidebar-nav">
          <div className="space-y-[2px]">
            {navItems.map((item) => (
              <div key={item.path}>
                <NavLink item={item} onClick={() => setSidebarOpen(false)} />
                {item.children &&
                  (location.pathname.startsWith("/analytics") || location.pathname === "/compare") && (
                    <div className="pl-9 py-1 space-y-[2px]" data-testid="analytics-submenu">
                      {item.children.map((child) => {
                        const isChildActive = location.pathname === child.path;
                        return (
                          <Link
                            key={child.path}
                            to={child.path}
                            onClick={() => setSidebarOpen(false)}
                            className={`block px-2 py-1 text-[12px] transition-colors ${
                              isChildActive
                                ? "text-[#0A0A0A] font-semibold"
                                : "text-[#888888] hover:text-[#0A0A0A]"
                            }`}
                            data-testid={`nav-analytics-sub-${child.label.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            {child.label}
                          </Link>
                        );
                      })}
                    </div>
                  )}
              </div>
            ))}
          </div>

          {/* Platforms Section */}
          <div className="mt-6 pt-4 border-t border-[#F0F0ED]">
            <p className="label-swiss px-5 mb-2">Plataformas</p>
            <div className="space-y-[2px]">
              {platformItems.map((platform) => {
                const isActive =
                  location.pathname === platform.path ||
                  (platform.children && platform.children.some((c) => location.pathname.startsWith(c.path)));
                const isExpanded = expandedPlatform === platform.path;

                return (
                  <div key={platform.path}>
                    {platform.children ? (
                      <button
                        onClick={() => setExpandedPlatform(isExpanded ? null : platform.path)}
                        className={`relative w-full flex items-center gap-3 pl-5 pr-3 py-2 text-[13px] transition-colors ${
                          isActive
                            ? "text-[#0A0A0A] font-semibold before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-[3px] before:h-4 before:bg-[#C8192C] before:rounded-r-sm"
                            : "text-[#888888] hover:text-[#0A0A0A]"
                        }`}
                        data-testid={`nav-platform-${platform.name.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <span
                          className="w-[10px] h-[10px] rounded-[2px] flex-shrink-0"
                          style={{ backgroundColor: platform.color }}
                        />
                        <span className="flex-1 text-left">{platform.name}</span>
                        <ChevronDown
                          className={`w-3.5 h-3.5 text-[#AAAAAA] transition-transform ${isExpanded ? "rotate-180" : ""}`}
                        />
                      </button>
                    ) : (
                      <Link
                        to={platform.path}
                        onClick={() => setSidebarOpen(false)}
                        className={`relative flex items-center gap-3 pl-5 pr-3 py-2 text-[13px] transition-colors ${
                          location.pathname === platform.path
                            ? "text-[#0A0A0A] font-semibold before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-[3px] before:h-4 before:bg-[#C8192C] before:rounded-r-sm"
                            : "text-[#888888] hover:text-[#0A0A0A]"
                        }`}
                        data-testid={`nav-${platform.name.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <span
                          className="w-[10px] h-[10px] rounded-[2px] flex-shrink-0"
                          style={{ backgroundColor: platform.color }}
                        />
                        <span>{platform.name}</span>
                      </Link>
                    )}

                    {platform.children && isExpanded && (
                      <div className="pl-9 py-1 space-y-[2px]" data-testid={`submenu-${platform.name.toLowerCase()}`}>
                        {platform.children.map((child) => {
                          const isChildActive = location.pathname === child.path;
                          return (
                            <Link
                              key={child.path}
                              to={child.path}
                              onClick={() => setSidebarOpen(false)}
                              className={`block px-2 py-1 text-[12px] transition-colors ${
                                isChildActive
                                  ? "text-[#0A0A0A] font-semibold"
                                  : "text-[#888888] hover:text-[#0A0A0A]"
                              }`}
                              data-testid={`nav-awin-sub-${child.label.toLowerCase()}`}
                            >
                              {child.label}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Admin link (only for admin roles) */}
          {user?.role && ["super_admin", "support_admin"].includes(user.role) && (
            <div className="mt-6 pt-4 border-t border-[#F0F0ED] px-5">
              <Link
                to="/admin"
                className="flex items-center gap-2 text-[12px] font-semibold text-[#C8192C] hover:underline"
                data-testid="nav-admin-panel"
              >
                <Shield className="w-3.5 h-3.5" />
                Admin Panel
              </Link>
            </div>
          )}
        </nav>

        {/* Trial badge */}
        {trialInfo && (
          <div className="px-3 pb-2">
            <Link to="/settings/billing">
              <div
                className={`flex items-center gap-2 rounded px-3 py-2 text-[11px] font-semibold border ${
                  trialInfo.endingSoon
                    ? "bg-red-50 border-red-200 text-red-700"
                    : "bg-amber-50 border-amber-200 text-amber-800"
                }`}
                data-testid="trial-badge"
              >
                <Clock className="w-3 h-3 shrink-0" />
                Prueba: quedan {trialInfo.daysLeft} {trialInfo.daysLeft === 1 ? "día" : "días"}
              </div>
            </Link>
          </div>
        )}

        {/* User Section */}
        <div className="px-3 py-3 border-t border-[#F0F0ED]">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-2.5 w-full p-2 rounded hover:bg-[#F7F6F3] transition-colors text-left"
                data-testid="profile-dropdown-trigger"
              >
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full" />
                ) : (
                  <div className="w-7 h-7 bg-[#F0F0ED] rounded-full flex items-center justify-center shrink-0">
                    <User className="w-3.5 h-3.5 text-[#888888]" strokeWidth={2} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-semibold text-[#0A0A0A] truncate leading-tight">
                    {user?.name || "Usuario"}
                  </p>
                  <p className="text-[11px] text-[#888888] truncate leading-tight">{user?.email}</p>
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-[#AAAAAA] shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start" className="w-56 mb-1">
              <DropdownMenuLabel>Mi cuenta</DropdownMenuLabel>
              <DropdownMenuSeparator />

              {/* Org Selector — only shown when user belongs to 2+ orgs */}
              {orgs.length > 1 && (
                <>
                  <DropdownMenuLabel className="text-xs text-[#888888] font-normal py-1 px-2">
                    Organización activa
                  </DropdownMenuLabel>
                  {orgs.map((org) => {
                    const isActive = org.organization_id === getActiveOrgId();
                    return (
                      <DropdownMenuItem
                        key={org.organization_id}
                        onClick={() => handleOrgSwitch(org.organization_id)}
                        className="flex items-center gap-2 cursor-pointer"
                        data-testid={`org-switch-${org.organization_id}`}
                      >
                        <Building2 className="w-4 h-4 text-[#888888] shrink-0" />
                        <span className="flex-1 truncate text-sm">{org.name}</span>
                        {isActive && <Check className="w-3.5 h-3.5 text-[#C8192C] shrink-0" />}
                      </DropdownMenuItem>
                    );
                  })}
                  <DropdownMenuSeparator />
                </>
              )}

              <DropdownMenuItem asChild>
                <Link to="/profile" onClick={() => setSidebarOpen(false)} data-testid="nav-profile">
                  <User className="w-4 h-4 mr-2" /> Perfil
                </Link>
              </DropdownMenuItem>
              {/* Integraciones y Facturación — solo para owners */}
              {isOwner && (
                <>
                  <DropdownMenuItem asChild>
                    <Link to="/integrations" onClick={() => setSidebarOpen(false)} data-testid="nav-integrations">
                      <Plug className="w-4 h-4 mr-2" /> Integraciones
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/settings/billing" onClick={() => setSidebarOpen(false)} data-testid="nav-billing">
                      <CreditCard className="w-4 h-4 mr-2" /> Facturación
                    </Link>
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuItem asChild>
                <Link to="/settings/team" onClick={() => setSidebarOpen(false)} data-testid="nav-team">
                  <Users className="w-4 h-4 mr-2" /> Equipo
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/notifications/telegram" onClick={() => setSidebarOpen(false)} data-testid="nav-telegram">
                  <Send className="w-4 h-4 mr-2" /> Notif. Telegram
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/tracking" onClick={() => setSidebarOpen(false)} data-testid="nav-tracking">
                  <Code2 className="w-4 h-4 mr-2" /> Tracking
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/alerts" onClick={() => setSidebarOpen(false)} data-testid="nav-alerts">
                  <Bell className="w-4 h-4 mr-2" /> Alertas
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-[#C8192C] focus:text-[#C8192C] focus:bg-[#FEEEF0] cursor-pointer"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" /> Cerrar Sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-[210px] min-h-screen bg-white">
        <div className="w-full px-6 py-6 md:px-8 md:py-8">{children}</div>
      </main>
    </div>
  );
};

export default DashboardLayout;
